import type { ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatTaka } from '@/utils/number';
import type { PcmpQueueItem } from '../../types/pcmpQueue.types';
import { LaneCell, PcmpEmptyCell, PersonCell, TagsCell } from './PcmpQueueCells';

interface PcmpQueueColumnOptions<T extends PcmpQueueItem> {
  /** Shows the "Optional" tag (Marketing's optional approval). */
  isOptional?: (item: T) => boolean;
  /** Columns placed before the ref (e.g. a selection checkbox). */
  leading?: ColumnDef<T>[] | undefined;
  /** Queue-specific columns placed after the amounts. */
  afterAmounts?: ColumnDef<T>[] | undefined;
  /** Queue-specific columns placed after the lane statuses. */
  afterLanes?: ColumnDef<T>[] | undefined;
  renderActions: (item: T) => ReactNode;
}

const emptyCell = <PcmpEmptyCell />;

/** The columns every PCMP proposal queue shows, with room for each queue's own. */
export function getPcmpQueueColumns<T extends PcmpQueueItem>({
  isOptional,
  leading = [],
  afterAmounts = [],
  afterLanes = [],
  renderActions,
}: PcmpQueueColumnOptions<T>): ColumnDef<T>[] {
  return [
    ...leading,
    {
      id: 'refNo',
      header: 'Ref',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-mono font-medium whitespace-nowrap">{row.original.refNo}</span>
          <span className="text-xs whitespace-nowrap text-muted-foreground">
            {formatDisplayDate(row.original.proposalDate)}
          </span>
        </div>
      ),
    },
    {
      id: 'tags',
      header: '',
      cell: ({ row }) => (
        <TagsCell hold={row.original.hold} isOptional={isOptional?.(row.original) ?? false} />
      ),
    },
    {
      id: 'mo',
      header: 'MO',
      cell: ({ row }) => <PersonCell person={row.original.mo} />,
    },
    {
      id: 'institute',
      header: 'Institute',
      cell: ({ row }) => {
        const { institute } = row.original;
        const place = [institute.thanaName, institute.districtName].filter(Boolean).join(', ');
        return (
          <div className="flex max-w-64 flex-col">
            <span className="truncate font-medium" title={institute.name ?? undefined}>
              {institute.name ?? EMPTY_VALUE}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {[institute.eiinNumber ? `EIIN ${institute.eiinNumber}` : null, place]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        );
      },
    },
    {
      id: 'teacher',
      header: 'Teacher',
      cell: ({ row }) => {
        const { teacher } = row.original;
        return (
          <div className="flex max-w-52 flex-col">
            <span className="truncate font-medium" title={teacher.name}>
              {teacher.name}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {[teacher.mobile, teacher.designation].filter(Boolean).join(' · ')}
            </span>
          </div>
        );
      },
    },
    {
      id: 'reporting',
      header: 'Reporting',
      cell: ({ row }) => <PersonCell person={row.original.reporting} />,
    },
    {
      id: 'projectName',
      header: 'Project',
      cell: ({ row }) => (
        <span className="block max-w-48 truncate" title={row.original.projectName}>
          {row.original.projectName}
        </span>
      ),
    },
    {
      id: 'proposedAmount',
      header: () => <div className="text-right">Proposed</div>,
      cell: ({ row }) => (
        <div className="text-right whitespace-nowrap tabular-nums">
          {formatTaka(row.original.proposedAmount)}
        </div>
      ),
    },
    {
      id: 'teacherDue',
      header: () => <div className="text-right">Due</div>,
      cell: ({ row }) =>
        row.original.teacherDue === null ? (
          <div className="text-right">{emptyCell}</div>
        ) : (
          <div className="text-right whitespace-nowrap tabular-nums">
            {formatTaka(row.original.teacherDue)}
          </div>
        ),
    },
    ...afterAmounts,
    {
      id: 'batch',
      header: 'Batch',
      cell: ({ row }) => {
        const { routeBatchNo, urgentBatchNo } = row.original;
        if (routeBatchNo === null && urgentBatchNo === null) return emptyCell;
        return (
          <div className="flex flex-col text-sm whitespace-nowrap tabular-nums">
            {routeBatchNo !== null ? <span>#{routeBatchNo}</span> : null}
            {urgentBatchNo !== null ? (
              <span className="text-xs text-muted-foreground">Urgent #{urgentBatchNo}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'marketing',
      header: 'Marketing',
      cell: ({ row }) => <LaneCell cell={row.original.status.lanes.marketing} />,
    },
    {
      id: 'mis',
      header: 'MIS',
      cell: ({ row }) => <LaneCell cell={row.original.status.lanes.mis} />,
    },
    {
      id: 'accounts',
      header: 'Accounts',
      cell: ({ row }) => <LaneCell cell={row.original.status.lanes.accounts} />,
    },
    {
      id: 'overall',
      header: 'Overall',
      cell: ({ row }) => <LaneCell cell={row.original.status.lanes.overall} />,
    },
    ...afterLanes,
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        // The row itself opens the proposal; keep button clicks from reaching it.
        <div
          className="flex justify-end gap-1.5"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          {renderActions(row.original)}
        </div>
      ),
    },
  ];
}
